import React, { useEffect, useState } from 'react';
import { Button, DatePicker, LoadingSpinner, MultiSelect } from 'yankees-react-utils';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import PlayerSelector from '../../../../components/PlayerSelector';
import { Stats, useOktaQuery } from '../../../../data';

const StyledHeader = styled.div`
    position: relative;
    z-index: 2;
    .dropdown-toggle.form-control {
        max-width: 100px;
    }

    .form-control {
        max-width: 130px;
    }
`;

const Header = ({ filter, setFilter, levels, setLevels }) => {
    const { t: translate } = useTranslation();
    const player = usePlayerSelector();

    const [innerPitcher, setInnerPitcher] = useState(filter.pitcher);
    const [innerLevelIds, setInnerLevelIds] = useState(filter.levelIds ?? []);
    const [innerStartDate, setInnerStartDate] = useState(filter.startDate);
    const [innerEndDate, setInnerEndDate] = useState(filter.endDate);

    const pitcherId = player.IsCatcher ? innerPitcher?.PlayerID : player.PlayerID;
    const { data: pitchingSeasons, isLoading } = useOktaQuery(
        ['pitchingSeasons', pitcherId],
        (t) => Stats.seasonPitching(t, { playerid: pitcherId }),
        { enabled: !!pitcherId, staleTime: 0, cacheTime: 0 },
    );
    useEffect(() => {
        if (pitchingSeasons && pitchingSeasons.length) {
            const localLevels = pitchingSeasons
                .map((ps) => ps.Level);
            // throw out dupes and sort by level sortorder
            const newLevels = [...new Set(localLevels.map((lv) => lv.LevelID))]
                .map((levelId) => localLevels.find((lv) => lv.LevelID === levelId))
                .sort((a, b) => a.SortOrder - b.SortOrder)
                .map((lv) => ({
                    name: lv.Abbr,
                    value: lv.LevelID,
                }));
            setLevels(newLevels);
            setInnerLevelIds([newLevels[0]?.value]);
            if (!player.IsCatcher) {
                setFilter({ ...filter, levelIds: [newLevels[0]?.value] });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pitchingSeasons]);

    useEffect(() => {
        setInnerLevelIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerPitcher]);

    useEffect(() => {
        setInnerPitcher(filter.pitcher);
    }, [filter.pitcher]);

    useEffect(() => {
        setInnerLevelIds(filter.levelIds ?? []);
    }, [filter.levelIds]);

    useEffect(() => {
        setInnerStartDate(filter.startDate);
    }, [filter.startDate]);

    useEffect(() => {
        setInnerEndDate(filter.endDate);
    }, [filter.endDate]);

    const onApply = () => {
        setFilter({
            ...filter,
            levelIds: innerLevelIds,
            startDate: innerStartDate,
            endDate: innerEndDate,
            pitcher: innerPitcher,
        });
    };

    return (
        <StyledHeader className="d-flex justify-content-between align-items-center mb-1">
            <h3>{translate('sections.reports.pitcher.bat-tracking-title')}</h3>
            <div className="rounded py-2 px-3 d-flex align-items-center bg-white" style={{ border: '1px solid rgb(188,200,209)' }}>
                {player.IsCatcher && (
                    <>
                        <div className="me-1">{translate('baseball.pitcher_one')}:</div>
                        <PlayerSelector
                            teamid={player.Team.TeamID}
                            position="pitcher"
                            player={innerPitcher}
                            onSelect={setInnerPitcher}
                            title={translate('common.select-a-pitcher')}
                        />
                    </>
                )}
                <div className="d-flex align-items-center me-4 ms-3">
                    <span className="me-1">{translate('baseball.level_one')}:</span>
                    {isLoading ? <LoadingSpinner /> : <MultiSelect value={innerLevelIds} setValue={setInnerLevelIds} options={levels} disabled={!pitcherId} />}
                </div>
                <div className="d-flex align-items-center">
                    <DatePicker
                        value={innerStartDate}
                        onSelect={setInnerStartDate}
                        size="sm"
                        maxDate={innerEndDate}
                    />
                    <div className="mx-2">{translate('common.to')}</div>
                    <DatePicker
                        value={innerEndDate}
                        onSelect={setInnerEndDate}
                        size="sm"
                        minDate={innerStartDate}
                    />
                </div>
                <Button className="ms-5" variant="primary" onClick={onApply}>{translate('common.apply')}</Button>
            </div>
        </StyledHeader>
    );
};

export default Header;
